import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ReferralCode {
  id: string;
  nursery_id: string;
  code: string;
  created_at: string;
}

export interface Referral {
  id: string;
  referrer_nursery_id: string;
  referred_nursery_id: string | null;
  code: string;
  status: string;
  referrer_rewarded_at: string | null;
  referred_rewarded_at: string | null;
  created_at: string;
  converted_at: string | null;
}

export interface ReferralReward {
  id: string;
  nursery_id: string;
  referral_id: string;
  reward_type: string;
  months_added: number;
  applied_at: string;
  stripe_coupon_id: string | null;
}

export function useReferralCode() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["referral-code", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referral_codes")
        .select("*")
        .limit(1)
        .single();
      if (error) return null;
      return data as ReferralCode;
    },
    enabled: !!user,
    staleTime: 300_000,
  });
}

export function useReferrals() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["referrals", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) return [];
      return (data || []) as Referral[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useReferralRewards() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["referral-rewards", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referral_rewards")
        .select("*")
        .order("applied_at", { ascending: false });
      if (error) return [];
      return (data || []) as ReferralReward[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useReferralStats() {
  const { data: referrals = [] } = useReferrals();
  const { data: rewards = [] } = useReferralRewards();

  const referred = referrals.filter(r => ["signed_up", "converted", "rewarded"].includes(r.status)).length;
  const converted = referrals.filter(r => ["converted", "rewarded"].includes(r.status)).length;
  const monthsEarned = rewards.reduce((sum, r) => sum + r.months_added, 0);

  return { referred, converted, monthsEarned };
}
